import { Component, computed, Input, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  StatsSettings,
  StatsContent,
  StatItem,
  SECTION_THEME,
} from './section-renderer.models';

/**
 * Full-width Stats section renderer.
 * Layout variants:
 * - simple: Clean row of stats without card styling
 * - cards: Stats in card containers with border/shadow
 * 
 * Stats structure follows the Team pattern:
 * - Settings: contains stat definitions (id, icon) - shared across languages
 * - Content: contains statTexts keyed by stat ID (value, label, description) - localized
 */
@Component({
  selector: 'dcp-stats-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section
      class="relative py-24 sm:py-32"
      [style.background-color]="theme.backgroundColor"
    >
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <!-- Section Header -->
        @if (content().title || content().subtitle) {
          <div class="mx-auto max-w-2xl lg:max-w-none text-center mb-16">
            @if (content().title) {
              <h2 class="text-3xl font-bold tracking-tight sm:text-4xl" [style.color]="theme.textColor">
                {{ content().title }}
              </h2>
            }
            @if (content().subtitle) {
              <p class="mt-4 text-lg leading-8" [style.color]="theme.textMutedColor">
                {{ content().subtitle }}
              </p>
            }
          </div>
        }

        <!-- Stats Grid -->
        <div class="mx-auto max-w-2xl lg:max-w-none">
          <dl [class]="gridClasses()">
            @for (stat of resolvedStats(); track stat.id) {
              @if (layoutVariant() === 'simple') {
                <!-- Simple variant: no card, just centered text -->
                <div class="flex flex-col items-center text-center p-6">
                  <dd class="text-4xl font-bold tracking-tight" [style.color]="theme.primaryColor">
                    {{ stat.value }}
                  </dd>
                  <dt class="mt-3 text-base font-medium" [style.color]="theme.textColor">
                    {{ stat.label }}
                  </dt>
                  @if (stat.description) {
                    <dd class="mt-2 text-sm" [style.color]="theme.textMutedColor">
                      {{ stat.description }}
                    </dd>
                  }
                </div>
              } @else {
                <!-- Cards variant: with border, background, shadow -->
                <div
                  class="flex flex-col items-center justify-center rounded-xl border p-8 text-center shadow-sm"
                  [style.background-color]="theme.surfaceColor"
                  [style.border-color]="theme.borderColor"
                >
                  <dd class="text-4xl font-bold tracking-tight" [style.color]="theme.primaryColor">
                    {{ stat.value }}
                  </dd>
                  <dt class="mt-3 text-base font-medium" [style.color]="theme.textColor">
                    {{ stat.label }}
                  </dt>
                  @if (stat.description) {
                    <dd class="mt-2 text-sm" [style.color]="theme.textMutedColor">
                      {{ stat.description }}
                    </dd>
                  }
                </div>
              }
            }
          </dl>
        </div>
      </div>
    </section>
  `,
})
export class StatsRendererComponent {
  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  protected get theme() { return this.themeSignal(); }

  private readonly _settings = signal<StatsSettings>({});
  private readonly _content = signal<StatsContent>({});

  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(value as StatsSettings);
  }

  @Input() set contentInput(value: Record<string, unknown>) {
    this._content.set(value as StatsContent);
  }

  readonly settings = this._settings.asReadonly();
  readonly content = this._content.asReadonly();

  readonly layoutVariant = computed(() => {
    const settings = this._settings();
    // Support both 'variant' (from editor) and 'layoutVariant' (legacy)
    return (settings as { variant?: string }).variant || settings.layoutVariant || 'simple';
  });

  /** Grid classes based on number of columns from settings */
  readonly gridClasses = computed(() => {
    const cols = this._settings().columns ?? 4;
    const colsMap: Record<number, string> = {
      2: 'grid grid-cols-1 gap-6 sm:grid-cols-2',
      3: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4',
    };
    return colsMap[cols] || colsMap[4];
  });

  /**
   * Resolve stats by combining settings (structure) with content (localized text).
   * Also supports legacy format where stats array was in content directly.
   */
  readonly resolvedStats = computed((): StatItem[] => {
    const settings = this._settings();
    const content = this._content();
    
    // New format: stats in settings, texts in content.statTexts
    if (settings.stats && settings.stats.length > 0) {
      const statTexts = content.statTexts || {};
      return settings.stats.map(stat => {
        const text = statTexts[stat.id] || { value: '', label: '' };
        return {
          id: stat.id,
          value: text.value || '',
          label: text.label || '',
          description: text.description,
          icon: stat.icon,
        };
      });
    }
    
    // Legacy format: stats array directly in content
    const legacyStats = (content as unknown as { stats?: Array<{ value: string; label: string; description?: string; icon?: string }> }).stats;
    if (legacyStats && legacyStats.length > 0) {
      return legacyStats.map((stat, index) => ({
        id: `legacy-${index}`,
        value: stat.value || '',
        label: stat.label || '',
        description: stat.description,
        icon: stat.icon,
      }));
    }
    
    return [];
  });
}
